import React, { useState, useEffect } from 'react';
import { FiUser, FiMapPin, FiMail, FiHome, FiPhone } from 'react-icons/fi';
import { GoHash } from 'react-icons/go';
import { FaBuilding } from 'react-icons/fa';
import { MdOutlineBusiness } from 'react-icons/md';
import { PiIdentificationCardBold } from 'react-icons/pi';
import InputField from '../components/ui/InputField';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import ImageUploader from '../components/ui/ImageUploader';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchCustomerById, updateCustomer } from '../redux/actions/customerActions';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import axiosInstance from '../axios';

// Define interfaces for form validation
interface FormErrors {
  companyName?: string;
  ownerName?: string;
  businessEmail?: string;
  gstNumber?: string;
  panNumber?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
}

// ValidationMessage component to display error messages
const ValidationMessage = ({ message }: { message?: string | undefined }) => {
  if (!message) return null;
  return <p className="mt-1 text-xs text-red-500">{message}</p>;
};

const EditCustomer = () => {
  const { customerId } = useParams();

  // Redux Hooks
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading } = useAppSelector(state => state.customers);
  const { user } = useAppSelector(state => state.auth);

  // Permission check - redirect if user doesn't have customerEdit permission
  useEffect(() => {
    // Function to check if user has customerEdit permission
    const checkCustomerEditPermission = () => {
      // If user is superAdmin, they have all permissions
      if (user?.designation === 'superAdmin') {
        return true;
      }

      // If no user or no permissions, deny access
      if (!user || !user.permissions) {
        return false;
      }

      let hasCustomerEditPermission = false;

      // Check if permissions is an object with numeric keys (like {0: {...}})
      if (typeof user.permissions === 'object' && '0' in user.permissions) {
        //@ts-ignore
        hasCustomerEditPermission = Boolean(user.permissions[0].customerEdit);
      }
      // Check if permissions is an array
      else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
        hasCustomerEditPermission = Boolean(user.permissions[0].customerEdit);
      }
      // Direct object access if neither of the above
      else if (typeof user.permissions === 'object') {
        hasCustomerEditPermission = Boolean(user.permissions.customerEdit);
      }

      return hasCustomerEditPermission;
    };

    // If user doesn't have permission, redirect and show toast
    if (!checkCustomerEditPermission()) {
      toast.error("You don't have permission to edit customers");
      navigate('/customers');
    }
  }, [user, navigate]);

  // Basic Information
  const [companyName, setCompanyName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [billingAttentionName, setBillingAttentionName] = useState('');

  // Contact Information
  const [businessEmail, setBusinessEmail] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');

  // Business Details
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [businessSector, setBusinessSector] = useState('');

  // Address Details
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');

  // Customer Status
  const [customerStatus, setCustomerStatus] = useState('ACTIVE');

  // Logo Upload State
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Loading and Error States
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form Validation States
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Track initial values state
  const [initialValues, setInitialValues] = useState({
    companyName: '',
    ownerName: '',
    billingAttentionName: '',
    businessEmail: '',
    billingEmail: '',
    phone: '',
    mobile: '',
    gstNumber: '',
    panNumber: '',
    customerSector: '',
    customerStatus: 'ACTIVE',
    logo: null as string | null,
    clientAddress: {
      address: '',
      city: '',
      state: '',
      pinCode: '',
      country: 'India',
    },
  });

  // Business Sectors Options
  const businessSectors = [
    '',
    'Manufacturing',
    'Information Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Retail',
    'Transportation',
    'Construction',
    'Agriculture',
    'Energy',
    'Entertainment',
    'Hospitality',
    'Other',
  ];

  // Indian States and Union Territories List
  const indianStates = [
    '',
    // States (28)
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    // Union Territories (8)
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ];

  // Fetch customer data when component mounts
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!customerId) {
        setLoadError('No customer ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const response = await dispatch(fetchCustomerById(customerId)).unwrap();
        if (response?.data) {
          const customer = response.data;
          // Set initial values
          const initialData = {
            companyName: customer.companyName || '',
            ownerName: customer.ownerName || '',
            billingAttentionName: customer.billingAttentionName || '',
            businessEmail: customer.businessEmail || '',
            billingEmail: customer.billingEmail || '',
            phone: customer.phone || '',
            mobile: customer.mobile || '',
            gstNumber: customer.gstNumber || '',
            panNumber: customer.panNumber || '',
            customerSector: customer.customerSector || '',
            customerStatus: customer.customerStatus || 'ACTIVE',
            logo: customer.logo || null,
            clientAddress: {
              address: customer.clientAddress?.address || '',
              city: customer.clientAddress?.city || '',
              state: customer.clientAddress?.state || '',
              pinCode: customer.clientAddress?.pinCode?.toString() || '',
              country: 'India',
            },
          };
          setInitialValues(initialData);

          // Populate form fields with customer data
          setCompanyName(customer.companyName || '');
          setOwnerName(customer.ownerName || '');
          setBillingAttentionName(customer.billingAttentionName || '');
          setBusinessEmail(customer.businessEmail || '');
          setBillingEmail(customer.billingEmail || '');
          setPhone(customer.phone || '');
          setMobile(customer.mobile || '');
          setGstNumber(customer.gstNumber || '');
          setPanNumber(customer.panNumber || '');
          setBusinessSector(customer.customerSector || '');
          setCustomerStatus(customer.customerStatus || 'ACTIVE');
          setLogoUrl(customer.logo || null);

          // Set address details
          if (customer.clientAddress) {
            setAddress(customer.clientAddress.address || '');
            setCity(customer.clientAddress.city || '');
            setState(customer.clientAddress.state || '');
            setPincode(customer.clientAddress.pinCode?.toString() || '');
          }
        }
      } catch (error: any) {
        setLoadError(error.message || 'Failed to fetch customer');
        toast.error(error.message || 'Failed to fetch customer');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerData();
  }, [customerId, dispatch]);

  // Handle Input Changes
  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>, field: string) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const value = e.target.value;

        // Special handling for pincode to only allow numbers
        if (field === 'pincode') {
          if (!/^\d*$/.test(value) || value.length > 6) return;
        }

        // Special handling for phone/mobile to only allow numbers
        if (field === 'phone' || field === 'mobile') {
          if (!/^\d*$/.test(value)) return;
        }

        setter(value);
        setTouched(prev => ({ ...prev, [field]: true }));
        validateField(field, value);
      };

  // Validate Single Field
  const validateField = (field: string, value: string) => {
    let error = '';

    switch (field) {
      case 'companyName':
        if (!value) error = 'Company name is required';
        break;
      case 'ownerName':
        if (!value) error = 'Owner name is required';
        break;
      case 'businessEmail':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = 'Invalid email format';
        }
        break;
      case 'panNumber':
        if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value)) {
          error = 'Invalid PAN number format';
        }
        break;
      case 'phone':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'Phone number must be 10 digits';
        }
        break;
      case 'mobile':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'Mobile number must be 10 digits';
        }
        break;
      case 'address':
        if (!value) error = 'Address is required';
        break;
      case 'city':
        if (!value) error = 'City is required';
        break;
      case 'state':
        if (!value) error = 'State is required';
        break;
      case 'pincode':
        if (!value) error = 'PIN code is required';
        else if (!/^\d{6}$/.test(value)) error = 'PIN code must be 6 digits';
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  // Handle Logo Upload
  const handleLogoChange = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoUrl(null);
    }
  };

  // Validate Form
  const validateForm = () => {
    const validations = [
      validateField('companyName', companyName),
      validateField('ownerName', ownerName),
      validateField('businessEmail', businessEmail),
      validateField('gstNumber', gstNumber),
      validateField('panNumber', panNumber),
      validateField('phone', phone),
      validateField('mobile', mobile),
      validateField('address', address),
      validateField('city', city),
      validateField('state', state),
      validateField('pincode', pincode),
    ];

    // Mark all fields as touched
    setTouched({
      companyName: true,
      ownerName: true,
      businessEmail: true,
      gstNumber: true,
      panNumber: true,
      phone: true,
      mobile: true,
      address: true,
      city: true,
      state: true,
      pincode: true,
    });

    return validations.every(Boolean);
  };

  // Handle Form Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }

    if (!customerId) {
      toast.error('No customer ID provided');
      return;
    }

    const currentValues = {
      companyName,
      ownerName,
      billingAttentionName,
      businessEmail,
      billingEmail,
      phone,
      mobile,
      gstNumber,
      panNumber,
      customerSector: businessSector,
      customerStatus,
      logo: logoUrl,
      clientAddress: {
        address,
        city,
        state,
        pinCode: pincode,
        country: 'India',
      },
    };

    // Compare and collect only changed values
    const changedValues: Record<string, any> = {};
    Object.keys(currentValues).forEach(key => {
      const current = currentValues[key as keyof typeof currentValues];
      const initial = initialValues[key as keyof typeof initialValues];

      if (key === 'clientAddress' && typeof current === 'object' && current !== null) {
        const changedAddress: Record<string, any> = {};
        let hasAddressChanges = false;

        const addressKeys = ['address', 'city', 'state', 'pinCode', 'country'] as const;
        addressKeys.forEach(addrKey => {
          const currentAddr = (current as Record<string, any>)[addrKey];
          const initialAddr =
            typeof initial === 'object' && initial !== null
              ? (initial as Record<string, any>)[addrKey]
              : undefined;

          if (JSON.stringify(currentAddr) !== JSON.stringify(initialAddr)) {
            changedAddress[addrKey] = currentAddr;
            hasAddressChanges = true;
          }
        });

        if (hasAddressChanges) {
          changedValues.clientAddress = changedAddress;
          changedValues.billingAddress = changedAddress;
        }
      } else if (JSON.stringify(current) !== JSON.stringify(initial)) {
        changedValues[key] = current;
      }
    });

    if (Object.keys(changedValues).length === 0) {
      toast.success('No changes to update');
      return;
    }

    // Use toast.promise with Redux action
    try {
      await toast.promise(
        dispatch(
          updateCustomer({
            id: customerId, // Use the ID directly without parseInt
            data: changedValues,
          })
        ).unwrap(),
        {
          loading: 'Updating customer...',
          success: result => result.message || 'Customer updated successfully!',
          error: err => {
            if (err.errors && Array.isArray(err.errors)) {
              err.errors.forEach((error: { field: string; message: string }, index: number) => {
                setTimeout(() => {
                  toast.error(`${error.field}: ${error.message}`, {
                    id: `update-error-${index}`,
                    duration: 5000,
                  });
                }, index * 100);
              });
            }
            return err.message || 'Failed to update customer';
          },
        }
      );

      navigate('/customers');
    } catch (error) {
      // Error is already handled by toast.promise
      console.error('Update failed:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <AiOutlineLoading3Quarters className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Error Loading Customer</h2>
        <p className="text-gray-600">{loadError}</p>
        <button
          onClick={() => navigate('/customers')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Return to Customers
        </button>
      </div>
    );
  }

  return (
    <ComponentWrapper icon={<FaBuilding />} title="Edit Customer">
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm">
        {/* Basic Information */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Company Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <InputField
                label="Company Name *"
                icon={<FaBuilding />}
                placeholder="Enter Company Name"
                value={companyName}
                onChange={handleInputChange(setCompanyName, 'companyName')}
              />
              <ValidationMessage message={touched.companyName ? errors.companyName : undefined} />
            </div>

            <div>
              <InputField
                label="Owner Name *"
                icon={<FiUser />}
                placeholder="Enter Owner Name"
                value={ownerName}
                onChange={handleInputChange(setOwnerName, 'ownerName')}
              />
              <ValidationMessage message={touched.ownerName ? errors.ownerName : undefined} />
            </div>

            <InputField
              label="Billing Attention Name"
              icon={<FiUser />}
              placeholder="Enter Billing Attention Name (optional)"
              value={billingAttentionName}
              onChange={handleInputChange(setBillingAttentionName, 'billingAttentionName')}
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <InputField
                label="Business Email"
                icon={<FiMail />}
                type="email"
                placeholder="Enter Business Email"
                value={businessEmail}
                onChange={handleInputChange(setBusinessEmail, 'businessEmail')}
              />
              <ValidationMessage
                message={touched.businessEmail ? errors.businessEmail : undefined}
              />
            </div>

            <InputField
              label="Billing Email"
              icon={<FiMail />}
              type="email"
              placeholder="Enter Billing Email (optional)"
              value={billingEmail}
              onChange={handleInputChange(setBillingEmail, 'billingEmail')}
            />

            <div>
              <InputField
                label="Phone Number"
                icon={<FiPhone />}
                placeholder="Enter Phone Number"
                value={phone}
                onChange={handleInputChange(setPhone, 'phone')}
              />
              <ValidationMessage message={touched.phone ? errors.phone : undefined} />
            </div>

            <div>
              <InputField
                label="Mobile Number"
                icon={<FiPhone />}
                placeholder="Enter Mobile Number (optional)"
                value={mobile}
                onChange={handleInputChange(setMobile, 'mobile')}
              />
              <ValidationMessage message={touched.mobile ? errors.mobile : undefined} />
            </div>
          </div>
        </div>

        {/* Company Logo Section */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Company Logo</h3>
          <ImageUploader
            onImageUpload={handleLogoChange}
            onImageRemove={() => handleLogoChange(null)}
            previewUrl={logoUrl}
            title="Drop company logo here, or click to upload"
            dragActiveTitle="Drop logo here"
          />
        </div>

        {/* Business Details */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Business Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <InputField
                label="GST Number"
                icon={<GoHash />}
                placeholder="Enter GST Number"
                value={gstNumber}
                onChange={handleInputChange(setGstNumber, 'gstNumber')}
              />
              <ValidationMessage message={touched.gstNumber ? errors.gstNumber : undefined} />
            </div>

            <div>
              <InputField
                label="PAN Number"
                icon={<PiIdentificationCardBold />}
                placeholder="Enter PAN Number"
                value={panNumber}
                onChange={handleInputChange(setPanNumber, 'panNumber')}
              />
              <ValidationMessage message={touched.panNumber ? errors.panNumber : undefined} />
            </div>

            <InputField
              label="Business Sector"
              type="select"
              icon={<MdOutlineBusiness />}
              placeholder="Select Business Sector"
              value={businessSector}
              onChange={handleInputChange(setBusinessSector, 'businessSector')}
              options={businessSectors}
            />
          </div>
        </div>

        {/* Address Information */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Address Information</h3>
          <div>
            <InputField
              label="Address *"
              type="textarea"
              placeholder="Enter Complete Address"
              value={address}
              onChange={handleInputChange(setAddress, 'address')}
              icon={<FiHome />}
            />
            <ValidationMessage message={touched.address ? errors.address : undefined} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            <div>
              <InputField
                label="City *"
                icon={<FiMapPin />}
                placeholder="Enter City"
                value={city}
                onChange={handleInputChange(setCity, 'city')}
              />
              <ValidationMessage message={touched.city ? errors.city : undefined} />
            </div>

            <div>
              <InputField
                label="State *"
                type="select"
                icon={<FiMapPin />}
                placeholder="Select State"
                value={state}
                onChange={handleInputChange(setState, 'state')}
                options={indianStates}
              />
              <ValidationMessage message={touched.state ? errors.state : undefined} />
            </div>

            <div>
              <InputField
                label="PIN Code *"
                icon={<FiMapPin />}
                placeholder="Enter PIN Code"
                value={pincode}
                onChange={handleInputChange(setPincode, 'pincode')}
              />
              <ValidationMessage message={touched.pincode ? errors.pincode : undefined} />
            </div>
          </div>
        </div>

        {/* Customer Status */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-4">Status</h3>
          <InputField
            label="Customer Status"
            type="select"
            placeholder="Select Status"
            value={customerStatus}
            onChange={handleInputChange(setCustomerStatus, 'customerStatus')}
            options={['', 'ACTIVE', 'INACTIVE']}
            className="w-full"
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            {loading ? (
              <>
                <AiOutlineLoading3Quarters className="w-5 h-5 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Customer'
            )}
          </button>
        </div>
      </form>
    </ComponentWrapper>
  );
};

export default EditCustomer;
